import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { TID } from "../constants/testIds";
import { ArrowRight, Clock, MapPin, User, CalendarDays } from "lucide-react";
import { resolveImageUrl } from "../lib/url";
import Seo from "../components/Seo";

const fmtSessionDate = (d) => {
  try {
    return new Date(d + "T00:00").toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return d; }
};

const FachbereichDetail = () => {
  const { slug } = useParams();
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get(`/public/departments/${slug}`)
      .then(r => setD(r.data))
      .catch(() => setErr("Fachbereich nicht gefunden"));
    api.get(`/public/departments/${slug}/upcoming?limit=8`)
      .then(r => setSessions(r.data || []))
      .catch(() => setSessions([]));
  }, [slug]);

  if (err) return <div className="py-32 text-center text-muted-foreground">{err}</div>;
  if (!d) return <div className="py-32 text-center text-muted-foreground">Lade…</div>;

  const courseJsonLd = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": d.name,
    "description": d.description,
    "image": d.image,
    "provider": {
      "@type": "Organization",
      "name": "KK Academy",
      "sameAs": "https://kkacademy.ch",
    },
    "offers": d.price_per_lesson ? {
      "@type": "Offer",
      "price": d.price_per_lesson,
      "priceCurrency": "CHF",
      "category": "per lesson",
    } : undefined,
    "courseMode": "in-person",
    "locationCreated": d.location || "Wil SG, Schweiz",
    "inLanguage": ["de", "ta"],
  };

  return (
    <div data-testid={`fachbereich-${slug}`}>
      <Seo
        title={`${d.name} – ${d.category === "music" ? "Musikunterricht" : "Tanzunterricht"} in Wil SG`}
        description={d.description?.slice(0, 160)}
        path={`/fachbereich/${slug}`}
        image={d.image}
        keywords={`${d.name}, ${d.name} Unterricht, ${d.name} Kurs, Tamilische ${d.category === "music" ? "Musik" : "Tanz"}`}
        jsonLd={courseJsonLd}
      />
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 w-full">
          <Link to={d.category === "music" ? "/musikakademie" : "/tanzakademie"} className="text-xs uppercase tracking-[0.3em] text-primary mb-4 inline-block hover:underline">
            ← {d.category === "music" ? t("nav.music") : t("nav.dance")}
          </Link>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-white">{d.name}</h1>
          {d.levels && d.levels.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-5">
              {d.levels.map(l => (
                <span key={l} className="text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/30 text-primary rounded-full px-3 py-1">{l}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("department.description")}</div>
              <p className="text-lg leading-relaxed text-foreground/90">{d.description}</p>
            </div>

            {d.gallery && d.gallery.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">{t("department.gallery")}</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {d.gallery.map((g) => (
                    <div key={g.id} className="aspect-[4/3] overflow-hidden rounded-lg">
                      <img src={resolveImageUrl(g.url)} alt={g.title} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            {sessions.length > 0 && (
              <div className="card-academy rounded-lg p-6" data-testid="upcoming-sessions">
                <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4 inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Nächste Termine</div>
                <ul className="space-y-2 text-sm">
                  {sessions.map(s => (
                    <li key={s.id} className={`flex items-start justify-between gap-3 pb-2 border-b border-border/40 last:border-0 last:pb-0 ${s.status === "cancelled" ? "opacity-50 line-through" : ""}`}>
                      <div>
                        <div className="font-medium">{fmtSessionDate(s.date)}</div>
                        {s.course_name && <div className="text-xs text-muted-foreground">{s.course_name}</div>}
                        {s.status === "moved" && s.note && <div className="text-[10px] text-amber-600 italic mt-1">verschoben · {s.note}</div>}
                        {s.status === "cancelled" && <div className="text-[10px] text-destructive italic mt-1">ausgefallen</div>}
                      </div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">{s.start_time}–{s.end_time}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="card-academy rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Details</div>
              <ul className="space-y-4 text-sm">
                {d.schedule && (
                  <li className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">{t("department.schedule")}</div>
                      <div>{d.schedule}</div>
                    </div>
                  </li>
                )}
                {d.location && (
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-primary mt-0.5" />
                    <div>
                      <div className="text-xs text-muted-foreground">{t("department.location")}</div>
                      <div>{d.location}</div>
                    </div>
                  </li>
                )}
                {d.price_per_lesson > 0 && (
                  <li className="flex items-start gap-3">
                    <div className="w-4 h-4 text-primary mt-0.5">CHF</div>
                    <div>
                      <div className="text-xs text-muted-foreground">{t("department.price")}</div>
                      <div>CHF {d.price_per_lesson} / {t("department.pricePerLesson")}</div>
                    </div>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <User className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <div className="text-xs text-muted-foreground">{t("department.status")}</div>
                    <div>{d.status === "active" ? t("department.active") : t("department.comingSoon")}</div>
                  </div>
                </li>
              </ul>
              <Link
                to={`/anmeldung?course=${encodeURIComponent(d.name)}&category=${d.category}`}
                data-testid={TID.deptEnrollBtn}
                className="mt-6 inline-flex w-full justify-center items-center gap-2 px-6 py-3 rounded-full btn-gold font-medium"
              >
                {t("department.enrollTitle")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {d.teacher && (
              <div className="card-academy rounded-lg overflow-hidden">
                <div className="aspect-square overflow-hidden">
                  <img src={d.teacher.photo} alt={d.teacher.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">{t("department.teacher")}</div>
                  <div className="font-serif-display text-xl">{d.teacher.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{d.teacher.role}</div>
                </div>
              </div>
            )}

            {d.partner && (
              <a
                href={d.partner.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`partner-${slug}`}
                className="card-academy rounded-lg p-6 block group"
              >
                <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--brand-green))] mb-4">In Zusammenarbeit mit</div>
                <div className="flex items-center gap-4 mb-3">
                  <img src={d.partner.logo} alt={d.partner.name} className="h-16 w-16 object-contain rounded-md bg-[hsl(28_12%_13%)] p-1" />
                  <div>
                    <div className="font-serif-display text-lg leading-tight">{d.partner.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 group-hover:text-primary transition-colors">{(d.partner.url || "").replace(/^https?:\/\//, "").replace(/\/$/, "")}</div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{d.partner.description}</p>
              </a>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default FachbereichDetail;
